import { defineStore } from 'pinia'
import { ref, computed } from 'vue' // Changed imports
import axiosInstance from '@/helpers/axios'
import { SwalSuccess, SwalError, SwalDelete } from '@/helpers/mySwal'
import { useJobTemplateStore } from './jobTemplate'
import { useRouter, useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { useJobDuration } from '@/composables/useJobDuration' // Import the new composable

export const useJobStore = defineStore('job', () => {
  const router = useRouter()
  const route = useRoute()
  const jobTemplateStore = useJobTemplateStore()
  const jobs = ref([])
  const job = ref({}) // This is the job for JobView
  const loading = ref(false)
  const isLoaded = ref(false)
  const isLoadedOne = ref(false)
  const statusOptions = ref(['חדשה', 'בתהליך', 'ממתינה לטיפול', 'הושלמה', 'בוטלה'])

  // Use the composable for the job being viewed in JobView
  const { duration: totalActualDurationForJob } = useJobDuration(job)

  function getJobStatus(job) {
    if (job.visits?.some((v) => v.status === 'מתוכנן')) return 'ביקור מתוכנן'
    if (job.visits?.some((v) => v.status === 'בתהליך' || v.status === 'מושהה')) return 'בתהליך'
    if (job.isNonBillable) return 'הושלמה (ללא חיוב)'
    if (!job.price) return 'ממתין למחיר'
    const totalPayments = job.payments?.reduce((sum, p) => sum + p.amount, 0) || 0
    if (totalPayments === 0) return 'ממתין לתשלום'
    if (totalPayments < job.price) return 'תשלום חלקי'
    return 'הושלמה'
  }

  const getStatusSeverity = (status) => {
    switch (status) {
      case 'ביקור מתוכנן':
        return 'info' // משהו עתידי - מצב ניטרלי חיובי
      case 'בתהליך':
        return 'warn' // עבודה באמצע - מצב פעיל
      case 'ממתין למחיר':
        return 'secondary' // מצב אדמיניסטרטיבי, טרם התחיל
      case 'ממתין לתשלום':
        return 'danger' // דרוש טיפול כספי
      case 'תשלום חלקי':
        return 'contrast' // ביניים – יש התקדמות אך לא הושלם
      case 'הושלמה (ללא חיוב)':
        return 'success'
      case 'הושלמה':
        return 'success' // מצב מושלם – סיום מוצלח
      default:
        return 'secondary' // ברירת מחדל למקרה של ערך לא צפוי
    }
  }

  const getAllJobs = async () => {
    if (loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await getAllJobs()
    } else if (!isLoaded.value) {
      await readAllJobs()
    }
  }

  const readAllJobs = async () => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get('/api/jobs')
      jobs.value = data
      isLoaded.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת עבודות נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const getOneJob = async (id) => {
    if (!id) return
    loading.value = true
    try {
      const { data } = await axiosInstance.get(`/api/jobs/${id}`)
      job.value = data
      isLoadedOne.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת עבודה נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const createJob = async (data) => {
    loading.value = true
    try {
      const { data: job } = await axiosInstance.post('/api/jobs', data)
      if (isLoaded.value) {
        isLoaded.value = false
      }

      // אם נוצרה תבנית, טען מחדש תבניות פעם הבאה
      if (!data.jobTemplateId && jobTemplateStore.isLoaded) jobTemplateStore.isLoaded = false

      await SwalSuccess.fire({
        title: 'עבודה חדשה נוצרה בהצלחה',
        // text: 'הנך מועבר לדף העבודה...',
        timer: 3000
      })

      return job.id
    } catch (error) {
      console.error(error)
      if (error.response && error.response.status === 400) {
        SwalError.fire({
          title: 'הוספת עבודה נכשלה',
          text: error.response.data.error
        })
      } else {
        SwalError.fire({
          title: 'הוספת עבודה נכשלה'
        })
      }
    } finally {
      loading.value = false
    }
  }

  const updateJob = async (data) => {
    loading.value = true
    try {
      const { data: updatedJob } = await axiosInstance.put(`/api/jobs/${data.id}`, data)

      if (isLoaded.value) {
        isLoaded.value = false
      }

      if (isLoadedOne.value && updatedJob.id === job.value.id) {
        job.value = updatedJob
      }

      SwalSuccess.fire({
        title: 'פרטי העבודה עודכנו בהצלחה'
      })

      return updatedJob
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'עדכון עבודה נכשל',
        text: error.response.data.error
      })
      return { error: error.response.data.error }
    } finally {
      loading.value = false
    }
  }

  const deleteJob = async (id) => {
    loading.value = true
    try {
      await axiosInstance.delete(`/api/jobs/${id}`)
      if (isLoaded.value) {
        jobs.value = jobs.value.filter((job) => job.id !== id)
      }

      if (isLoadedOne.value) {
        job.value = null
        if (route.path === '/jobs/' + id) {
          router.push({ name: 'jobs' })
        }
      }

      SwalSuccess.fire({
        title: 'פרטי העבודה נמחקו בהצלחה'
      })
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'מחיקת עבודה נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const confirmDelete = async (id) => {
    const confirm = await SwalDelete.fire({
      text: `האם אתה בטוח שברצונך למחוק את העבודה?
      כל הביקורים של העבודה יימחקו.`
    })

    if (confirm.isConfirmed) {
      await deleteJob(id)
    }
  }

  const getVisitActualDurationInSeconds = (visit) => {
    if (!visit || !visit.actualStartTime) {
      return 0
    }

    const from = dayjs(visit.actualStartTime)
    let to
    if (visit.actualEndTime) {
      to = dayjs(visit.actualEndTime)
    } else if (visit.lastPauseTime && visit.status === 'מושהה') {
      to = dayjs(visit.lastPauseTime)
    } else if (visit.status === 'בתהליך') {
      to = dayjs() // current time
    } else {
      return 0
    }

    let secondsDuration = to.diff(from, 'seconds')
    if (visit.totalPausedDuration) {
      secondsDuration -= visit.totalPausedDuration
    }

    return secondsDuration < 0 ? 0 : secondsDuration
  }

  const getJobTotalActualDuration = (jobData) => {
    if (!jobData || !jobData.visits || jobData.visits.length === 0) {
      return '00:00:00'
    }

    const totalSeconds = jobData.visits.reduce((acc, visit) => {
      return acc + getVisitActualDurationInSeconds(visit)
    }, 0)

    return dayjs.duration(totalSeconds, 'seconds').format('HH:mm:ss')
  }

  const $reset = () => {
    jobs.value = []
    job.value = {}
    loading.value = false
    isLoaded.value = false
    isLoadedOne.value = false
  }

  return {
    jobs,
    job,
    loading,
    isLoaded,
    isLoadedOne,
    statusOptions,
    totalActualDurationForJob,
    getJobStatus,
    getStatusSeverity,
    getAllJobs,
    getOneJob,
    createJob,
    updateJob,
    deleteJob,
    confirmDelete,
    getJobTotalActualDuration,
    $reset
  }
})
